import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAssessmentEdit } from '@/hooks/useAssessmentEdit';
import { EditAssessmentTabs } from '@/components/admin/assessment/EditAssessmentTabs';
import { EditAssessmentActions } from '@/components/admin/assessment/EditAssessmentActions';
import { VersionChangeConfirmDialog } from '@/components/admin/assessment/VersionChangeConfirmDialog';
import { VersionHistoryDialog } from '@/components/admin/assessment/VersionHistoryDialog';
import { revertToVersion } from '@/services/assessment';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';

export default function EditAssessment() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('categories');
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { toast } = useToast();
  
  const {
    loading,
    title,
    setTitle,
    description,
    setDescription,
    slug,
    setSlug,
    categories,
    setCategories,
    isActive,
    setIsActive,
    isMaturityModel,
    notifyEmails,
    setNotifyEmails,
    calendlyLink,
    setCalendlyLink,
    formatSlug,
    assessment,
    handleSubmit,
    handlePublish,
    isContentChanged,
    confirmingVersionChange,
    cancelContentChanges,
    saveContent,
    autoSaveContent
  } = useAssessmentEdit(id);

  // Track unsaved changes
  useEffect(() => {
    if (assessment) {
      const hasChanges = 
        title !== assessment.title ||
        description !== assessment.description ||
        slug !== assessment.slug ||
        isActive !== assessment.isActive ||
        notifyEmails !== (assessment.notifyEmails || '') ||
        calendlyLink !== (assessment.calendlyLink || '') ||
        isContentChanged();
      
      setHasUnsavedChanges(hasChanges);
    }
  }, [title, description, slug, isActive, notifyEmails, calendlyLink, categories, assessment, isContentChanged]);

  const onSave = async () => {
    const success = await handleSubmit();
    // Don't manually set hasUnsavedChanges here - let the useEffect handle it
    // based on the updated assessment data
  };

  const onPublish = async () => {
    const success = await handlePublish();
    // Don't manually set hasUnsavedChanges here - let the useEffect handle it
    // based on the updated assessment data
  };

  // Improved version confirmation handler
  const handleVersionConfirm = async () => {
    const success = await saveContent();
    // Don't manually set hasUnsavedChanges here - let the useEffect handle it
    // based on the updated assessment data
  };

  const handleRevertToVersion = async (versionNumber: number) => {
    if (!id) return;
    
    try {
      setRevertLoading(true);
      const updatedAssessment = await revertToVersion(id, versionNumber);
      
      if (updatedAssessment) {
        // Refresh the page to show updated data
        window.location.reload();
        
        toast({
          title: "Version Reverted",
          description: `Successfully reverted to version ${versionNumber}`
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to revert to the selected version"
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error occurred while reverting version"
      });
    } finally {
      setRevertLoading(false);
      setVersionHistoryOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {assessment && (
        <div className="flex items-center justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVersionHistoryOpen(true)}
          >
            <Clock className="mr-2 h-4 w-4" />
            Version History
          </Button>
        </div>
      )}

      <EditAssessmentTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        title={title}
        setTitle={setTitle}
        description={description}
        setDescription={setDescription}
        slug={slug}
        setSlug={setSlug}
        formatSlug={formatSlug}
        isActive={isActive}
        setIsActive={setIsActive}
        isMaturityModel={isMaturityModel}
        notifyEmails={notifyEmails}
        setNotifyEmails={setNotifyEmails}
        calendlyLink={calendlyLink}
        setCalendlyLink={setCalendlyLink}
        categories={categories}
        setCategories={setCategories}
        assessment={assessment}
        onContentChange={autoSaveContent}
        loading={loading}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={onSave}
        onPublish={onPublish}
      />

      {/* Only show actions on settings tab for form submission */}
      {activeTab === 'settings' && (
        <EditAssessmentActions 
          id={id || ''} 
          loading={loading} 
          onSubmit={onSave}
          onPublish={onPublish}
          assessment={assessment}
        />
      )}

      {/* Version change confirmation dialog */}
      {assessment && (
        <VersionChangeConfirmDialog
          isOpen={confirmingVersionChange}
          onClose={cancelContentChanges}
          onConfirm={handleVersionConfirm}
          currentVersion={assessment.version}
        />
      )}

      {/* Version history dialog */}
      {assessment && assessment.versionHistory && assessment.versionHistory.length > 0 && (
        <VersionHistoryDialog
          assessment={assessment}
          isOpen={versionHistoryOpen}
          onClose={() => setVersionHistoryOpen(false)}
          onRevertToVersion={handleRevertToVersion}
          loading={revertLoading}
        />
      )}
    </div>
  );
}
